import { useQuery, useMutation, useQueryClient } from 'react-query'
import { getPickingListsByJobId, createPickingListLine, submitPickingListPicks } from '../../api/picking_lists'
import type { CreatePickingListLinePayload, CreatePickingListLineResponse } from '../../api/picking_lists'
import { PickingListRowShape, PickingListSubmitResult, StagedPick } from '../../api/api_data_types'
import mergePickingListRecords from '../../components/files/mergePickingListRecords'
import type { ApiError } from '../../components/helpers/error'

export function usePickingListsQuery(jobId: string) {
  return useQuery(['picking_lists', jobId], () => getPickingListsByJobId(jobId))
}

export function useCreatePickingListLineMutation() {
  const queryClient = useQueryClient()
  const reload = () => queryClient.invalidateQueries('picking_lists')
  const mutationFunction = (values: CreatePickingListLinePayload) => createPickingListLine(values)

  return useMutation<CreatePickingListLineResponse, ApiError, CreatePickingListLinePayload>(mutationFunction, { onSuccess: reload })
}

export function useSubmitPickingListPicksMutation(jobId: string, changedQuantities: Record<string, StagedPick>) {
  const queryClient = useQueryClient()
  const reload = () => queryClient.invalidateQueries('picking_lists')

  const mutationFunction = () => {
    const picks = Object.values(changedQuantities).map((pick) => ({
      item_no: pick.item_no,
      item_id: pick.item_id,
      quantity: pick.quantity,
    }))
    return submitPickingListPicks({ job_id: jobId, picks })
  }

  return useMutation<PickingListSubmitResult, ApiError, void>(mutationFunction, {
    onSuccess: async (result) => {
      await queryClient.cancelQueries(['picking_lists', jobId])
      queryClient.setQueryData<PickingListRowShape[]>(['picking_lists', jobId], (current) =>
        mergePickingListRecords(current, result.records)
      )
      // Keep the existing refresh, without waiting for NAV to show locally saved picks.
      void reload()
    },
  })
}
